/**
 * Display formatting.
 *
 * The app is INR-only, formatted with Indian digit grouping (1,30,996.44) —
 * the convention used in inventory/prototype. `Number()` is used ONLY to feed
 * Intl for display; arithmetic on quantities or money always goes through the
 * decimal helpers in @gsls/shared.
 */
import type { DecimalString } from '@gsls/shared';

const money = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const quantity = new Intl.NumberFormat('en-IN', {
  maximumFractionDigits: 3,
});

const dateTime = new Intl.DateTimeFormat('en-IN', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const dateOnly = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' });

const EMPTY = '—';

export function formatMoney(value: DecimalString | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? money.format(parsed) : EMPTY;
}

export function formatQuantity(
  value: DecimalString | null | undefined,
  uom?: string | null,
): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return EMPTY;
  const formatted = quantity.format(parsed);
  return uom ? `${formatted} ${uom}` : formatted;
}

/** Unit cost always shows its unit of measure: ₹412.00/kg */
export function formatUnitCost(value: DecimalString | null | undefined, uom?: string | null): string {
  const formatted = formatMoney(value);
  if (formatted === EMPTY || !uom) return formatted;
  return `${formatted}/${uom}`;
}

/**
 * A ledger quantity, with its direction always visible. In the movement table a
 * bare "10 kg" would not say whether stock came in or went out.
 */
export function formatSignedQuantity(
  value: DecimalString | null | undefined,
  uom?: string | null,
): string {
  const formatted = formatQuantity(value, uom);
  if (formatted === EMPTY) return formatted;
  return formatted.startsWith('-') ? formatted : `+${formatted}`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? EMPTY : dateTime.format(parsed);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EMPTY;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? EMPTY : dateOnly.format(parsed);
}

/** Sentence-case a snake_case enum value for display: raw_material -> Raw material. */
export function humanise(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
